import React, { useRef, useState } from "react";
// reactstrap components
import {
    Card,
    CardHeader,
    CardBody,
    Container,
    Row,
    Col,
    Button
} from "reactstrap";
// core components
import Header from "../../../components/headers/Header";
import LoadingIndicator from "../../../components/ui/LoadingIndicator";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useForm } from "react-hook-form"
import { Upload, Icon, message, Button as AntdBtn } from "antd";
import "antd/dist/antd.css";
import { UploadOutlined } from "@ant-design/icons";

import { Actions, reducer, INITIAL_STATE, ActionTypes } from "../../../redux/screens/admin/language/EditRedux";
import AuthorizedScreen from "../../../components/base/AuthorizedScreen";
import { NavigationService, NavigateActionTypes } from "../../../navigation";
import UIDs from "../../../UIDs";

const uid = UIDs.screens.admin.language.edit;

class EditScreen extends AuthorizedScreen {
    get routeName() {
        return uid;
    }

    get reducer() {
        return reducer;
    }

    get actionHandlers() {
        return {
            [ActionTypes.LOAD_SCREEN_VIEW_ASYNC]: { done: null, error: this._handleAsyncActionError },
            [ActionTypes.SAVE_LANGUAGE_ASYNC]: { done: this._handleSaveLanguageAsyncDone, error: this._handleAsyncActionError },
            [ActionTypes.UPLOAD_LOGO_ASYNC]: { done: null, error: this._handleAsyncActionError }
        };
    }

    localVars = {
        navParams: {}
    };

    constructor(props) {
        super(props);

        this.localVars.navParams = NavigationService.getNavParams(props);
    }

    componentDidMount() {
        super.componentDidMount();

        const { code } = this.localVars.navParams;
        if (code)
            this.props.loadScreenViewAsync(code);
        else
            this.props.createLanguage();
    }

    render() {
        const { language } = this.props.localState;
        return (
            <>
                <Header />
                <Container className="mt--7" fluid>
                    <Row>
                        <div className="col">
                            {!language || this.isActionExecuting(ActionTypes.LOAD_SCREEN_VIEW_ASYNC)
                                ? <LoadingIndicator />
                                : <EditForm
                                    {...this.props}
                                    language={language}
                                    onCancel={this._handleCancelEdit}
                                    onSave={this._handleSaveLanguage}
                                    onUpload={this.props.uploadLogoAsync}
                                    isDisabled={this.isActionExecuting(ActionTypes.SAVE_LANGUAGE_ASYNC) || this.isActionExecuting(ActionTypes.UPLOAD_LOGO_ASYNC)}
                                />
                            }
                        </div>
                    </Row>
                </Container>
            </>
        );
    }

    _handleCancelEdit = () => {
        NavigationService.navigateByAction(this, NavigateActionTypes.BACK);
    }

    _handleSaveLanguage = (data) => {
        const { language } = this.props.localState;
        data = { ...language, ...data };
        confirmAlert({
            title: this.props.t("confirmToSave"),
            message: this.props.t("sureToDo"),
            buttons: [
                {
                    label: this.props.t("yes"),
                    onClick: () => {
                        this.props.saveLanguageAsync(data);
                    }
                },
                {
                    label: this.props.t("no")
                }
            ]
        });
    }

    _handleSaveLanguageAsyncDone = () => {
        NavigationService.navigateByAction(this, NavigateActionTypes.BACK);
    }

    _handleAsyncActionError = (action) => {
        this.displayError(action.error);
    }
}

function EditForm(props) {
    const { language } = props;
    const { name, code, imageUrl } = language;
    const { register, handleSubmit, errors } = useForm();
    const [loading, setLoading] = useState(false);
    const submitRef = useRef();

    const uploadButton = (
        <AntdBtn>
            {loading ? <Icon type="loading" /> : <UploadOutlined />}{props.t("upload")}
        </AntdBtn>
    );

    const uploadProps = {
        name: "fileData",
        showUploadList: false, // set to only upload one file        
        customRequest: info => {
            props.onUpload(info.file);
        },
        onChange: info => {
            if (info.file.status === "uploading") {
                setLoading(true);
            }
        },
        listType: "picture-card",
        className: "avatar-uploader",
        beforeUpload: file => {
            const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
            if (!isJpgOrPng) {
                message.error(props.t("uploadImageFormatLimited"));
                return;
            }
            const isLt2M = file.size / 1024 / 1024 < 2;
            if (!isLt2M) {
                message.error(props.t("uploadImageSizeLimited", { fileSize: 2 }));
                return;
            }
            return isJpgOrPng && isLt2M;
        }
    };

    return (
        <Card className="shadow">
            <CardHeader className="bg-white border-0">
                <Row className="align-items-center">
                    <Col xs="8">
                        <h3 className="mb-0">{props.t("language")}</h3>
                    </Col>
                    <Col className="text-right" xs="4">
                        <Button
                            color="info"
                            onClick={() => submitRef.current.click()}
                            disabled={props.isDisabled}
                            size="sm">
                            {props.t("save")}
                        </Button>&nbsp;
                        <Button
                            color="default"
                            onClick={props.onCancel}
                            disabled={props.isDisabled}
                            size="sm">
                            {props.t("cancel")}
                        </Button>
                    </Col>
                </Row>
            </CardHeader>
            <CardBody>
                <form onSubmit={handleSubmit(props.onSave)}>
                    <div className="pl-lg-4">
                        <div className="row">
                            <div className="col-lg-12">
                                <div className="form-group">
                                    <label className="form-control-label">{props.t("logo")}</label>
                                    <Upload {...uploadProps}>
                                        {!imageUrl ? uploadButton : <img src={imageUrl} alt="..." style={{ width: "100%" }} />}
                                    </Upload>
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-lg-12">
                                <div className="form-group">
                                    <label className="form-control-label">{props.t("code")}</label>
                                    <input name="code" type="text" className="form-control-alternative form-control" defaultValue={code}
                                      ref={register({ required: true })}
                                      />
                                      {errors.code && <span className="text-danger">{props.t("fieldIsRequired")}</span>}
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-lg-12">
                                <div className="form-group">
                                    <label className="form-control-label">{props.t("name")}</label>
                                    <input name="name" type="text" className="form-control-alternative form-control" defaultValue={name}
                                        ref={register({ required: true })}
                                    />
                                    {errors.name && <span className="text-danger">{props.t("fieldIsRequired")}</span>}
                                </div>
                            </div>
                        </div>
                    </div>
                    <input ref={submitRef} type="submit" hidden />
                </form>
            </CardBody>
        </Card>
    );
}

const mapStateToProps = state => {
    return {
        localState: state.screens[reducer.key] ? state.screens[reducer.key] : INITIAL_STATE,
        userId: state.session.userId
    };
};

const mapDispatchToProps = dispatch => {
    return {
        loadScreenViewAsync: code => dispatch(Actions.loadScreenViewAsync(code)),
        saveLanguageAsync: vo => dispatch(Actions.saveLanguageAsync(vo)),
        createLanguage: () => dispatch(Actions.createLanguage()),
        uploadLogoAsync: file => dispatch(Actions.uploadLogoAsync(file))
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation(uid)(EditScreen));